<script lang="ts" setup>
import type { UiGap } from '../types';
import { gapClasses, paddingClasses, type UiJustification, type UiPadding } from '../ui-layout/classes';

defineOptions({
    inheritAttrs: false
});

type ModalLayer = 'default' | 'nested';
type ModalViewport = 'default' | 'inset' | 'workspace';
type ModalContentLayout = 'default' | 'fill';
type ModalBodyLayout = 'default' | 'stack' | 'grid';
type ModalBodyOverflow = 'auto' | 'hidden' | 'visible';
type ModalHeaderDensity = 'compact' | 'default' | 'spacious';
type ModalFooterLayout = 'standard' | 'content';

const props = withDefaults(defineProps<{
    id?: string;
    title?: string;
    fullscreen?: boolean;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
    hideHeader?: boolean;
    hideFooter?: boolean;
    hideClose?: boolean;
    keepRendered?: boolean;
    layer?: ModalLayer;
    viewport?: ModalViewport;
    contentLayout?: ModalContentLayout;
    bodyLayout?: ModalBodyLayout;
    bodyPadding?: UiPadding;
    bodyGap?: UiGap;
    bodyOverflow?: ModalBodyOverflow;
    bodyHeight?: 'auto' | 'fill' | 'viewport';
    headerDensity?: ModalHeaderDensity;
    footerPadding?: UiPadding;
    footerJustify?: UiJustification;
    footerLayout?: ModalFooterLayout;
    elevated?: boolean;
    hidden?: boolean;
}>(), {
    title: null,
    size: 'md',
    layer: 'default',
    viewport: 'default',
    contentLayout: 'default',
    bodyLayout: 'default',
    bodyPadding: 'md',
    bodyGap: 'sm',
    bodyOverflow: 'auto',
    bodyHeight: 'auto',
    headerDensity: 'default',
    footerPadding: 'md',
    footerJustify: 'end',
    footerLayout: 'standard',
    elevated: false,
    hidden: false
});

const emit = defineEmits<{
    (e: 'ok'): void;
    (e: 'close'): void;
    (e: 'hidden'): void;
}>();

const generatedId = useId();
const dialogRef = ref<HTMLDialogElement | null>(null);
const dialog = useDialog(dialogRef);
const isRendered = ref(false);
const wasRendered = ref(false);
const okReturnValue = 'ok';
const closeReturnValue = 'close';
const closeAnimationFallbackDelay = 250;

let closeAnimationTimeout: number | null = null;
let closeAnimationCleanup: (() => void) | null = null;

const dialogId = computed(() => props.id ?? `ideo-modal-${generatedId}`);

const dialogClasses = computed(() => [
    {
        'fullscreen !m-0 h-dvh max-h-none w-dvw rounded-none bg-white dark:bg-black': flag(props.fullscreen),
        'z-4 backdrop:z-3': props.layer === 'nested',
        'm-2 h-[calc(100dvh-1rem)] min-h-[calc(100dvh-1rem)] max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-[calc(100%-1rem)] sm:m-7 sm:h-[calc(100dvh-3.5rem)] sm:min-h-[calc(100dvh-3.5rem)] sm:max-h-[calc(100dvh-3.5rem)] sm:w-[calc(100%-3.5rem)] sm:max-w-[calc(100%-3.5rem)]': props.viewport === 'inset',
        '!m-6 !h-[calc(100dvh-48px)] !w-[calc(100dvw-48px)] !rounded-md': props.viewport === 'workspace',
        'pointer-events-none invisible': props.hidden
    }
]);

function flag(value: any)
{
    return value !== false;
}

const contentClasses = computed(() => [
    'content ideo-modal-content flex max-h-[inherit] flex-col overflow-hidden rounded-[inherit] bg-white dark:bg-black',
    {
        'h-full min-h-full': flag(props.fullscreen) || props.contentLayout === 'fill',
        'shadow-lg': props.elevated
    }
]);

const headerDensityClasses: Record<ModalHeaderDensity, string> = {
    compact: 'p-2 text-sm',
    default: 'p-3 text-base',
    spacious: 'p-4 text-base'
};

const bodyLayoutClasses: Record<ModalBodyLayout, string> = {
    default: '',
    stack: 'flex flex-col',
    grid: 'grid'
};

const bodyOverflowClasses: Record<ModalBodyOverflow, string> = {
    auto: 'overflow-x-hidden overflow-y-auto',
    hidden: 'overflow-hidden',
    visible: 'overflow-visible'
};

const bodyHeightClasses: Record<'auto' | 'fill' | 'viewport', string> = {
    auto: 'flex-[0_1_auto]',
    fill: 'flex-1',
    viewport: 'h-[calc(100dvh-56px)] flex-none'
};

const bodyClasses = computed(() => [
    'ideo-modal-body min-h-0',
    paddingClasses[props.bodyPadding],
    bodyLayoutClasses[props.bodyLayout],
    props.bodyLayout === 'default' ? '' : gapClasses[props.bodyGap],
    bodyOverflowClasses[props.bodyOverflow],
    bodyHeightClasses[props.bodyHeight]
]);
const shouldRenderContent = computed(() => isRendered.value || (flag(props.keepRendered) && wasRendered.value));

const modalWidth = computed(() =>
{
    if (flag(props.fullscreen) || props.viewport !== 'default')
    {
        return '100%';
    }

    switch (props.size)
    {
        case 'sm': return '300px';
        case 'md': return '500px';
        case 'lg': return '800px';
        case 'xl': return '1140px';
        case 'xxl': return '1400px';
        default: return '500px';
    }
});

defineExpose(dialog);

function emitHidden()
{
    nextTick(() =>
    {
        emit('hidden');
    });
}

function clearCloseAnimation()
{
    if (closeAnimationTimeout !== null)
    {
        window.clearTimeout(closeAnimationTimeout);
        closeAnimationTimeout = null;
    }

    closeAnimationCleanup?.();
    closeAnimationCleanup = null;
}

function finishClose()
{
    clearCloseAnimation();
    isRendered.value = false;
    emitHidden();
}

function waitForCloseAnimation(currentDialog: HTMLDialogElement)
{
    clearCloseAnimation();

    const onTransitionEnd = (event: TransitionEvent) =>
    {
        if (event.target !== currentDialog || event.propertyName !== 'opacity')
        {
            return;
        }

        finishClose();
    };

    currentDialog.addEventListener('transitionend', onTransitionEnd);
    closeAnimationCleanup = () => currentDialog.removeEventListener('transitionend', onTransitionEnd);
    closeAnimationTimeout = window.setTimeout(finishClose, closeAnimationFallbackDelay);
}

function onCloseAction()
{
    if (!dialogRef.value || !dialogRef.value.open)
    {
        return;
    }

    dialogRef.value.close(closeReturnValue);
}

function onOkAction()
{
    if (!dialogRef.value || !dialogRef.value.open)
    {
        return;
    }

    dialogRef.value.close(okReturnValue);
}

function onNativeOpen()
{
    clearCloseAnimation();
    isRendered.value = true;
    wasRendered.value = true;
}

function onNativeClose(event: Event)
{
    const currentDialog = event.target as HTMLDialogElement;
    const returnValue = currentDialog.returnValue;

    if (returnValue === okReturnValue)
    {
        emit('ok');
    }
    else if (returnValue === closeReturnValue)
    {
        emit('close');
    }

    currentDialog.returnValue = '';
    waitForCloseAnimation(currentDialog);
}

onBeforeUnmount(() =>
{
    clearCloseAnimation();

    if (dialogRef.value?.open)
    {
        dialogRef.value.returnValue = '';
        dialogRef.value.close();
    }
});
</script>

<template>
    <dialog
        :id="dialogId"
        ref="dialogRef"
        v-bind="$attrs"
        class="dialog ideo-modal-dialog m-auto w-full max-w-full scale-90 overflow-visible rounded-md border-0 bg-white dark:bg-black p-0 text-gray-700 dark:text-neutral-100 opacity-0 [&:focus:not(:focus-visible)]:outline-none [&:focus-visible]:outline-none"
        :class="dialogClasses"
        :style="{ maxWidth: modalWidth }"
        @ideo-modal-open="onNativeOpen"
        @close="onNativeClose"
    >
        <div v-if="shouldRenderContent" :class="contentClasses" @click.stop>
            <header v-if="!flag(hideHeader)" :class="['flex items-center justify-between border-b border-gray-200 dark:border-neutral-800', headerDensityClasses[props.headerDensity]]">
                <slot name="modal-header">
                    <IdeoHeading tag="h5" class="title m-0 text-base font-medium text-gray-950 dark:text-neutral-200">
                        {{ title }}
                    </IdeoHeading>
                    <button
                        v-if="!flag(hideClose)"
                        type="button"
                        class="close flex size-8 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-gray-700 transition-colors hover:bg-gray-100 dark:text-neutral-100 dark:hover:bg-neutral-800"
                        :aria-label="$t('[[[Zamknij]]]')"
                        @click.stop="onCloseAction"
                    >
                        <IdeoIcon name="x" size="24" />
                    </button>
                </slot>
            </header>
            <main :class="bodyClasses">
                <slot name="default" />
            </main>
            <footer v-if="!flag(hideFooter)" :class="['border-t border-gray-200 dark:border-neutral-800', paddingClasses[props.footerPadding]]">
                <IdeoButtonGroup :class="props.footerLayout === 'standard' ? 'w-full' : ''" :justify="props.footerJustify">
                    <slot
                        name="modal-footer"
                        :ok="function () { onOkAction(); }"
                        :cancel="function () { onCloseAction(); }"
                    >
                        <IdeoButton variant="secondary" icon="x" @click.stop="onCloseAction">
                            <slot name="btn-close">
                                {{ $t('[[[Zamknij]]]') }}
                            </slot>
                        </IdeoButton>
                        <IdeoButton variant="primary" icon="check" @click.stop="onOkAction">
                            <slot name="btn-submit">
                                {{ $t('[[[Ok]]]') }}
                            </slot>
                        </IdeoButton>
                    </slot>
                </IdeoButtonGroup>
            </footer>
        </div>
        <div class="teleport-target contents" data-ideo-modal-teleport-target />
    </dialog>
</template>

<style scoped>
.ideo-modal-dialog {

    max-block-size: calc(100dvh - 40px);
    transition:
    opacity 0.2s ease-in-out,
    scale 0.2s ease-in-out;
}

.ideo-modal-dialog::backdrop {
    pointer-events: none;
    background-color: color-mix(in srgb, #000 40%, transparent);
    @supports (color: color-mix(in lab, red, red)) {
        background-color: color-mix(in oklab, var(--color-black, #000) 40%, transparent);
    }
    opacity: 0%;

    backdrop-filter: blur(3px);
    transition:
    opacity 0.2s ease-in-out,
    backdrop-filter 0.2s ease-in-out;
}

.ideo-modal-dialog[open] {
    --tw-scale-x: 100%;
    --tw-scale-y: 100%;
    --tw-scale-z: 100%;
    scale: var(--tw-scale-x) var(--tw-scale-y);
    opacity: 100%;
}

.ideo-modal-dialog[open]::backdrop {
    opacity: 100%;
}

@starting-style {
    .ideo-modal-dialog[open] {
        --tw-scale-x: 90%;
        --tw-scale-y: 90%;
        opacity: 0%;
    }

    .ideo-modal-dialog[open]::backdrop {
        opacity: 0%;
    }
}

.ideo-modal-dialog.fullscreen {
    /* fullscreen znosi limit wysokości bazowego dialogu, inaczej zostaje szpara u dołu */
    max-block-size: none;
}

.ideo-modal-dialog.fullscreen::backdrop {
    backdrop-filter: none;
}
</style>
